import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { cn } from '@rocco/utils/cn';

interface GateStickyFooterProps {
    children: React.ReactNode;
    /**
     * Applied on both surfaces — leading spacing a step wants either way.
     *
     * Leading spacing only. A pinning surface sets its own padding, and this
     * lands after it on the sheet but before it on a pinned card, so a `pt-*` or
     * `pb-*` here would not agree between the two. Every caller passes `mt-*`,
     * which collides with nothing.
     */
    className?: string;
    /**
     * Applied only to the desktop card, for trailing spacing the card wants and
     * the sheet does not.
     *
     * A pinning surface owns its own bottom spacing, so a `mb-*` here is dropped
     * when that surface pins — on the sheet because this never reaches it, and
     * on a `pinOn="both"` card because it would otherwise win over the negative
     * margin that carries the bar to the pane's edge.
     */
    desktopClassName?: string;
    /**
     * Which surfaces pin the action.
     *
     * `mobile` is the default and the signed-off behaviour: the 977x635 card is
     * tall enough that a step's action rarely leaves the pane, so it scrolls as
     * part of the step. `both` is for a step long enough to overflow the card
     * too — today only the enquiry form, which carries a message field and the
     * BuildHub project-details block.
     */
    pinOn?: 'mobile' | 'both';
}

/**
 * What makes it a bar rather than a floating control: an opaque surface the
 * step's content passes behind, and a hairline to separate the two.
 */
/**
 * `z-20` because the whole point of the bar is to sit above the form scrolling
 * under it, and a sticky element without one is painted in DOM order — any
 * field that carries a z-index of its own (Rocco's avatar cluster is `z-10`)
 * otherwise shows through the bar's own background.
 */
const SURFACE = 'sticky bottom-0 z-20 border-t border-black/5 bg-white pt-4';

/**
 * Tells the pane's scroll indicator this bar is pinned and never scrolls, so
 * its track ends above the bar — see `SCROLL_PINNED_ATTRIBUTE` in Rocco.
 */
const PINNED = { 'data-scroll-pinned': '' };

/**
 * Holds a step's primary action against the bottom edge of the mobile sheet.
 *
 * `sticky` inside the sheet's own scroll container, which is why this needs no
 * portal and no slot on TwoPaneModal: when the step is taller than the sheet
 * the action stays in reach — including above the keyboard, since the sheet
 * shrinks to sit on top of it — and when the step is shorter the footer just
 * sits in flow.
 *
 * Inert on desktop. The 977x635 card is signed off with the action scrolling as
 * part of the step, and its pane is tall enough that it rarely has to.
 *
 * `-mb-6` cancels the sheet body's bottom padding so the bar reaches the
 * sheet's edge; the padding below restores that spacing and adds the device
 * safe area on top. Keep it in step with TwoPaneModal's sheet padding.
 */
export const GateStickyFooter = ({
    children,
    className,
    desktopClassName,
    pinOn = 'mobile',
}: GateStickyFooterProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);

    if (!isMobile) {
        if (pinOn === 'mobile') {
            return (
                <div className={cn(className, desktopClassName)}>
                    {children}
                </div>
            );
        }

        return (
            <div
                {...PINNED}
                className={cn(
                    className,
                    // Ahead of the surface, so its own trailing margin cannot
                    // beat the one below. Everything else it carries still lands.
                    desktopClassName,
                    SURFACE,
                    // No bottom padding of its own: `-mb-7` already cancels the
                    // body's, and the strip painted below covers the gap that
                    // leaves. Padding here as well put the action a full body
                    // padding further from the edge than anything else in the
                    // card.
                    '-mb-7',
                    // `bottom-0` sticks to the scrollport's padding edge, which
                    // the body's `py-7` puts 7 above the card's own edge — so
                    // the action floated well clear of the pane beside it. The
                    // pane is inset `my-3.5`, so this lands their bottom edges
                    // on the same line.
                    '-bottom-3.5',
                    // The card body carries its `py-7` on the scrolling element
                    // itself, so `bottom-0` sticks the bar to the padding edge
                    // and leaves that padding as a strip the step can still
                    // scroll through. Paint it rather than move the bar: the
                    // same token as the padding, so it tracks with the fluid
                    // root instead of guessing a pixel offset.
                    //
                    // A shadow, not an `::after`. `sticky` makes this element a
                    // containing block, so an absolutely-positioned strip below
                    // it counts toward the scroller's overflow area — every
                    // step was left permanently scrollable by the height of
                    // that strip, whether or not it had anything below the
                    // fold. A shadow is ink only: it never enters layout.
                    'shadow-[0_1.75rem_0_0_#fff]',
                )}
            >
                {children}
            </div>
        );
    }

    return (
        <div
            {...PINNED}
            className={cn(
                SURFACE,
                '-mb-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]',
                className,
            )}
        >
            {children}
        </div>
    );
};
